import { selectCurrentUser } from '../../../slices/userSlice';

/**
 * SecurityService - Client-side integration with SecurityRegistryManager
 * Provides comprehensive security validation for messaging system
 */
class SecurityService {
  constructor() {
    this.securityDefinitions = new Map();
    this.lastRefresh = null;
    this.isInitialized = false;
    this.baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';
  }

  /**
   * Initialize security service
   */
  async initialize() {
    try {
      await this.refreshSecurityDefinitions();
      this.isInitialized = true;
      console.log('SecurityService initialized successfully');
      return { success: true };
    } catch (error) {
      console.error('Failed to initialize SecurityService:', error);
      throw error;
    }
  }

  /**
   * Get authentication headers
   * Uses the same pattern as securityRegistrySlice for consistency
   */
  getAuthHeaders() {
    // Use the same token retrieval pattern as the working admin components
    const token = localStorage.getItem('authToken') || localStorage.getItem('token');
    return {
      'Content-Type': 'application/json',
      'Authorization': token ? `Bearer ${token}` : ''
    };
  }

  /**
   * Check if user has valid authentication token
   */
  hasValidToken() {
    const token = localStorage.getItem('authToken') || localStorage.getItem('token');
    return !!token;
  }

  /**
   * Refresh security definitions from server with enhanced error handling
   */
  async refreshSecurityDefinitions() {
    try {
      // Check if we have a valid token before making the request
      if (!this.hasValidToken()) {
        const errorMessage = 'No authentication token available';
        console.warn('SecurityService:', errorMessage);
        return { success: false, error: errorMessage, statusCode: 401 };
      }

      const response = await fetch(`${this.baseUrl}/security-registry/definitions`, {
        headers: this.getAuthHeaders()
      });

      if (!response.ok) {
        // Handle specific HTTP status codes
        if (response.status === 401) {
          const errorMessage = 'Authentication required - user may not have admin privileges or token may be expired';
          console.warn('SecurityService: 401 Unauthorized -', errorMessage);
          return { success: false, error: errorMessage, statusCode: 401 };
        } else if (response.status === 403) {
          const errorMessage = 'Access forbidden - user does not have admin privileges for security registry';
          console.warn('SecurityService: 403 Forbidden -', errorMessage);
          return { success: false, error: errorMessage, statusCode: 403 };
        } else {
          throw new Error(`Failed to fetch security definitions: ${response.status} ${response.statusText}`);
        }
      }

      const data = await response.json();

      if (data.success && data.definitions) {
        // Convert array to Map for efficient lookups
        this.securityDefinitions.clear();
        data.definitions.forEach(def => {
          this.securityDefinitions.set(def.name, def);
        });

        this.lastRefresh = new Date();
        console.log(`SecurityService: Successfully loaded ${data.definitions.length} security definitions`);
      }

      return { success: true, count: data.definitions?.length || 0 };
    } catch (error) {
      console.error('SecurityService: Failed to refresh security definitions:', error);
      // Don't throw - allow graceful degradation
      return { success: false, error: error.message };
    }
  }

  /**
   * Get security definition by name
   */
  getSecurityDefinition(name) {
    return this.securityDefinitions.get(name) || null;
  }

  /**
   * Get security definitions by entity type
   */
  getDefinitionsByEntityType(entityType) {
    return Array.from(this.securityDefinitions.values())
      .filter(def => def.entityType === entityType);
  }

  /**
   * Validate message security based on comprehensive security rules
   */
  validateMessageSecurity(messageData, userContext) {
    try {
      const {
        messageType,
        senderId,
        recipientId,
        senderRole,
        recipientRole,
        content,
        metadata = {}
      } = messageData;

      const validation = {
        valid: true,
        errors: [],
        warnings: [],
        securityLevel: 'LOW',
        auditRequired: false,
        restrictions: []
      };

      // Get security definition for message type
      const securityDef = this.getSecurityDefinition(`MESSAGE_${messageType}`);

      if (securityDef) {
        // Apply comprehensive security validation
        this.validateWithSecurityDefinition(messageData, securityDef, userContext, validation);
      } else {
        // Apply basic validation for undefined message types
        this.validateBasicMessageSecurity(messageData, userContext, validation);
      }

      // Additional context-based validations
      this.validateUserContext(userContext, validation);
      this.validateContentSecurity(content, validation);
      this.validateMetadataSecurity(metadata, validation);

      return validation;
    } catch (error) {
      console.error('Message security validation error:', error);
      return {
        valid: false,
        errors: [`Security validation failed: ${error.message}`],
        warnings: [],
        securityLevel: 'CRITICAL',
        auditRequired: true,
        restrictions: ['BLOCK_MESSAGE']
      };
    }
  }

  /**
   * Validate message against security definition
   */
  validateWithSecurityDefinition(messageData, securityDef, userContext, validation) {
    const { messageType, senderRole, recipientRole, metadata = {} } = messageData;

    // Set security level from definition
    validation.securityLevel = securityDef.criticality || 'MEDIUM';
    validation.auditRequired = securityDef.criticality === 'CRITICAL' || securityDef.criticality === 'HIGH';

    // Validate operations
    if (securityDef.operations) {
      // Check CREATE operation (sending message)
      if (securityDef.operations.CREATE) {
        const createRules = securityDef.operations.CREATE;

        // Role-based validation
        if (createRules.allowedRoles && !createRules.allowedRoles.includes(senderRole?.toUpperCase())) {
          validation.valid = false;
          validation.errors.push(`Role ${senderRole} not authorized to send ${messageType} messages`);
        }

        // Criticality-based restrictions
        if (createRules.requiresApproval && securityDef.criticality === 'CRITICAL') {
          validation.restrictions.push('REQUIRES_APPROVAL');
        }

        // Time-based restrictions
        if (createRules.timeRestrictions) {
          const timeValidation = this.validateTimeRestrictions(createRules.timeRestrictions);
          if (!timeValidation.valid) {
            validation.warnings.push(`Time restriction: ${timeValidation.message}`);
          }
        }
      }

      // Check READ operation (receiving message)
      if (securityDef.operations.READ) {
        const readRules = securityDef.operations.READ;

        if (readRules.allowedRoles && !readRules.allowedRoles.includes(recipientRole?.toUpperCase())) {
          validation.valid = false;
          validation.errors.push(`Role ${recipientRole} not authorized to receive ${messageType} messages`);
        }
      }
    }

    // Validate entity-specific rules
    if (securityDef.entityType === 'MESSAGE' && securityDef.entityRules) {
      this.validateEntityRules(messageData, securityDef.entityRules, validation);
    }

    // Apply security constraints
    if (securityDef.constraints) {
      this.validateConstraints(messageData, securityDef.constraints, validation);
    }
  }

  /**
   * Validate basic message security for undefined types
   */
  validateBasicMessageSecurity(messageData, userContext, validation) {
    const { senderRole, recipientRole, messageType } = messageData;

    // Basic role validation
    if (!senderRole || !recipientRole) {
      validation.warnings.push('Missing role information for security validation');
    }

    // Unknown message type warning
    if (messageType && !this.securityDefinitions.has(`MESSAGE_${messageType}`)) {
      validation.warnings.push(`No security definition found for message type: ${messageType}`);
      validation.securityLevel = 'MEDIUM'; // Default to medium security
    }

    // Basic user authorization
    if (!userContext?.authorized) {
      validation.valid = false;
      validation.errors.push('User not authorized for messaging operations');
    }
  }

  /**
   * Validate user context
   */
  validateUserContext(userContext, validation) {
    if (!userContext) {
      validation.valid = false;
      validation.errors.push('Missing user context for security validation');
      return;
    }

    // Check user authorization status
    if (!userContext.authorized && userContext.primaryRole !== 'guest') {
      validation.valid = false;
      validation.errors.push('User authorization required');
    }

    // Validate user permissions
    if (userContext.permissions && Array.isArray(userContext.permissions)) {
      const hasMessagingPermission = userContext.permissions.some(perm =>
        perm.includes('message') || perm.includes('send') || perm.includes('communication')
      );

      if (!hasMessagingPermission && userContext.primaryRole !== 'admin') {
        validation.warnings.push('User may lack specific messaging permissions');
      }
    }
  }

  /**
   * Validate content security
   */
  validateContentSecurity(content, validation) {
    if (!content || typeof content !== 'string') {
      validation.errors.push('Message content is required and must be a string');
      return;
    }

    // Content length validation
    if (content.length > 10000) {
      validation.warnings.push('Message content exceeds recommended length (10,000 characters)');
    }

    // Basic content security checks
    const suspiciousPatterns = [
      /\b(password|token|secret|key)\s*[:=]\s*\S+/i,
      /\b\d{4}[-\s]?\d{4}[-\s]?\d{4}[-\s]?\d{4}\b/, // Credit card pattern
      /\b\d{3}-\d{2}-\d{4}\b/ // SSN pattern
    ];

    suspiciousPatterns.forEach(pattern => {
      if (pattern.test(content)) {
        validation.warnings.push('Message content may contain sensitive information');
        validation.securityLevel = 'HIGH';
        validation.auditRequired = true;
      }
    });
  }

  /**
   * Validate metadata security
   */
  validateMetadataSecurity(metadata, validation) {
    if (!metadata || typeof metadata !== 'object') {
      return; // Metadata is optional
    }

    // Check for sensitive metadata
    const sensitiveKeys = ['password', 'token', 'secret', 'key', 'credential'];
    const metadataKeys = Object.keys(metadata).map(k => k.toLowerCase());

    const hasSensitiveData = sensitiveKeys.some(key =>
      metadataKeys.some(metaKey => metaKey.includes(key))
    );

    if (hasSensitiveData) {
      validation.warnings.push('Message metadata may contain sensitive information');
      validation.auditRequired = true;
    }
  }

  /**
   * Validate time restrictions
   */
  validateTimeRestrictions(timeRestrictions) {
    const now = new Date();
    const currentHour = now.getHours();
    const currentDay = now.getDay(); // 0 = Sunday, 6 = Saturday

    if (timeRestrictions.allowedHours) {
      const [startHour, endHour] = timeRestrictions.allowedHours;
      if (currentHour < startHour || currentHour > endHour) {
        return {
          valid: false,
          message: `Messages only allowed between ${startHour}:00 and ${endHour}:00`
        };
      }
    }

    if (timeRestrictions.allowedDays) {
      if (!timeRestrictions.allowedDays.includes(currentDay)) {
        return {
          valid: false,
          message: 'Messages not allowed on this day of the week'
        };
      }
    }

    return { valid: true };
  }

  /**
   * Validate entity rules
   */
  validateEntityRules(messageData, entityRules, validation) {
    // Implement entity-specific validation rules
    if (entityRules.maxContentLength && messageData.content?.length > entityRules.maxContentLength) {
      validation.valid = false;
      validation.errors.push(`Message content exceeds maximum length of ${entityRules.maxContentLength} characters`);
    }

    if (entityRules.requiredFields) {
      entityRules.requiredFields.forEach(field => {
        if (!messageData[field]) {
          validation.valid = false;
          validation.errors.push(`Required field missing: ${field}`);
        }
      });
    }
  }

  /**
   * Validate constraints
   */
  validateConstraints(messageData, constraints, validation) {
    // Rate limiting
    if (constraints.rateLimit) {
      // This would typically check against a rate limiting service
      validation.warnings.push('Rate limiting constraints apply to this message type');
    }

    // Approval requirements
    if (constraints.requiresApproval) {
      validation.restrictions.push('REQUIRES_APPROVAL');
    }

    // Encryption requirements
    if (constraints.requiresEncryption) {
      validation.restrictions.push('REQUIRES_ENCRYPTION');
    }
  }

  /**
   * Check if user can send specific message type
   */
  async canSendMessage(messageType, userContext, recipientContext = null) {
    try {
      const messageData = {
        messageType,
        senderId: userContext?.userId,
        recipientId: recipientContext?.userId,
        senderRole: userContext?.primaryRole || userContext?.role,
        recipientRole: recipientContext?.primaryRole || recipientContext?.role,
        content: '', // Empty for permission check
        metadata: {}
      };

      const validation = this.validateMessageSecurity(messageData, userContext);

      return {
        allowed: validation.valid,
        restrictions: validation.restrictions,
        warnings: validation.warnings,
        securityLevel: validation.securityLevel
      };
    } catch (error) {
      console.error('Error checking send message permission:', error);
      return {
        allowed: false,
        restrictions: ['SECURITY_ERROR'],
        warnings: [`Security check failed: ${error.message}`],
        securityLevel: 'CRITICAL'
      };
    }
  }

  /**
   * Check if user can receive specific message type
   */
  async canReceiveMessage(messageType, userContext) {
    try {
      const securityDef = this.getSecurityDefinition(`MESSAGE_${messageType}`);

      if (!securityDef) {
        return { allowed: true, warnings: [`No security definition for ${messageType}`] };
      }

      const userRole = userContext?.primaryRole || userContext?.role;

      if (securityDef.operations?.READ?.allowedRoles) {
        const allowed = securityDef.operations.READ.allowedRoles.includes(userRole?.toUpperCase()) ||
          userRole === 'admin';

        return {
          allowed,
          restrictions: allowed ? [] : ['ROLE_RESTRICTION'],
          securityLevel: securityDef.criticality || 'MEDIUM'
        };
      }

      return { allowed: true, securityLevel: securityDef.criticality || 'MEDIUM' };
    } catch (error) {
      console.error('Error checking receive message permission:', error);
      return {
        allowed: false,
        restrictions: ['SECURITY_ERROR'],
        warnings: [`Security check failed: ${error.message}`],
        securityLevel: 'CRITICAL'
      };
    }
  }

  /**
   * Log security audit event
   */
  async logSecurityAudit(eventType, eventData) {
    try {
      // This would typically send to a security audit service
      console.log('Security Audit Event:', {
        type: eventType,
        timestamp: new Date(),
        data: eventData
      });

      // In a real implementation, this would send to the server
      // await fetch(`${this.baseUrl}/api/security-registry/audit`, {
      //   method: 'POST',
      //   headers: this.getAuthHeaders(),
      //   body: JSON.stringify({ eventType, eventData })
      // });

    } catch (error) {
      console.error('Failed to log security audit event:', error);
    }
  }

  /**
   * Get security status
   */
  getSecurityStatus() {
    return {
      isInitialized: this.isInitialized,
      definitionCount: this.securityDefinitions.size,
      lastRefresh: this.lastRefresh,
      securityLevel: this.securityDefinitions.size > 0 ? 'ACTIVE' : 'DEGRADED'
    };
  }
}

// Create singleton instance
const securityService = new SecurityService();

export default securityService;
